import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api';
import { GearLink } from '../ui/GearLink';
import { formatWhen, NOTIFICATIONS_KEY, useOpenNotification, type Inbox, type InboxItem } from './types';
import { useEscapeLayer } from '../ui/Dialog';

/** How many newest messages the bell shows; the rest is on "Wszystkie powiadomienia". */
const PREVIEW = 8;
const REFRESH_MS = 60_000;

/** The unread count (refreshed about every minute and when the tab comes back) and the newest messages. */
export function useInboxPreview() {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'preview'],
    queryFn: () => api<Inbox>(`/api/me/notifications?limit=${PREVIEW}`),
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Messages as a list in style A: a dot for unread, the subject, the text and a small grey date. */
export function InboxList({ items, onOpen, className = '' }: { items: InboxItem[]; onOpen: (n: InboxItem) => void; className?: string }) {
  return (
    <ul className={`inbox ${className}`.trim()}>
      {items.map((n) => (
        <li key={n.id}>
          <button type="button" className={`inbox-item${n.readAt ? '' : ' unread'}`} onClick={() => onOpen(n)}>
            <span className="inbox-dot" aria-hidden="true" />
            <span className="inbox-text">
              <span className="inbox-subject">{n.subject}</span>
              {n.body && <span className="inbox-body">{n.body}</span>}
              <span className="inbox-when">
                {formatWhen(n.createdAt)}
                {!n.readAt && <span className="visually-hidden">, nieprzeczytane</span>}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15zM10 20.5a2 2 0 0 0 4 0" />
    </svg>
  );
}

/** The bell in the app frame: unread count, and a list of the newest messages. */
export function Bell() {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inbox = useInboxPreview();
  const location = useLocation();
  const openMessage = useOpenNotification(() => setOpen(false));
  const unread = inbox.data?.unread ?? 0;

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);
  const close = () => {
    setOpen(false);
    rootRef.current?.querySelector<HTMLElement>('button[aria-controls]')?.focus();
  };
  useEscapeLayer(close, open);

  const items = inbox.data?.notifications ?? [];
  return (
    <div className="bell" ref={rootRef} data-feature-id="notifications.inbox">
      <button
        type="button"
        className="bell-button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={unread > 0 ? `Powiadomienia: ${unread} nieprzeczytane` : 'Powiadomienia'}
        onClick={() => setOpen((v) => !v)}
      >
        <BellIcon />
        {unread > 0 && (
          <span className="bell-count" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="bell-panel" id={panelId} role="region" aria-label="Najnowsze powiadomienia">
          <div className="bell-head">
            <strong>Powiadomienia</strong>
            {unread > 0 && <span className="muted">{unread} nieprzeczytane</span>}
            <GearLink to="/moje-konto" className="bell-settings" label="Ustawienia powiadomień (telefon, komputer)" onClick={() => setOpen(false)} />
          </div>
          {inbox.isError && <p className="alert" role="alert">Nie udało się wczytać powiadomień</p>}
          {inbox.data && items.length === 0 && <p className="muted bell-empty">Nie masz powiadomień.</p>}
          {items.length > 0 && (
            <InboxList items={items} onOpen={openMessage} />
          )}
          <Link to="/powiadomienia" className="bell-all" onClick={() => setOpen(false)}>
            Wszystkie powiadomienia
          </Link>
        </div>
      )}
    </div>
  );
}
